import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Card, CardContent } from '@voilajsx/uikit/card';
import { Info } from 'lucide-react';

const PageLayoutTutorial = () => {
  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">PageLayout</h1>
          <p className="text-lg text-muted-foreground">
            PageLayout is for marketing sites, landing pages, and content-focused websites.
          </p>
        </div>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Visual Structure</h2>

          <Card>
            <CardContent className="pt-6">
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="bg-primary/20 p-3 text-center text-sm font-medium border-b border-border">
                  Header (sticky optional)
                </div>
                <div className="bg-background p-8 min-h-[200px] flex items-center justify-center text-muted-foreground">
                  Main Content Area
                </div>
                <div className="bg-primary/20 p-3 text-center text-sm font-medium border-t border-border">
                  Footer
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Basic Usage</h2>

          <CodePreview
            title="Simple PageLayout"
            code={`import { PageLayout } from '@voilajsx/uikit/page-layout';

function LandingPage() {
  return (
    <PageLayout
      header={<Header />}
      footer={<Footer />}
    >
      <section className="py-20 text-center">
        <h1 className="text-5xl font-bold mb-4">Welcome</h1>
        <p className="text-xl text-muted-foreground">
          Build amazing products
        </p>
      </section>
    </PageLayout>
  );
}`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Props Reference</h2>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Prop</th>
                  <th className="text-left p-3 font-semibold">Type</th>
                  <th className="text-left p-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">header</td>
                  <td className="p-3 text-muted-foreground">ReactNode</td>
                  <td className="p-3 text-muted-foreground">Header component</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">footer</td>
                  <td className="p-3 text-muted-foreground">ReactNode</td>
                  <td className="p-3 text-muted-foreground">Footer component</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">stickyHeader</td>
                  <td className="p-3 text-muted-foreground">boolean</td>
                  <td className="p-3 text-muted-foreground">Make header stick on scroll</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">maxWidth</td>
                  <td className="p-3 text-muted-foreground">string</td>
                  <td className="p-3 text-muted-foreground">Content max width (e.g., "6xl")</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3 font-mono">centered</td>
                  <td className="p-3 text-muted-foreground">boolean</td>
                  <td className="p-3 text-muted-foreground">Center content horizontally</td>
                </tr>
                <tr>
                  <td className="p-3 font-mono">className</td>
                  <td className="p-3 text-muted-foreground">string</td>
                  <td className="p-3 text-muted-foreground">Additional CSS classes</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">With Sticky Header</h2>

          <CodePreview
            title="Sticky Header Example"
            code={`<PageLayout
  header={<Header />}
  footer={<Footer />}
  stickyHeader={true}
>
  {/* Header stays visible while scrolling */}
  <main>Long content here...</main>
</PageLayout>`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Header Component Example</h2>

          <CodePreview
            title="Typical Header"
            code={`import { Button } from '@voilajsx/uikit/button';
import { Link } from 'react-router-dom';

function Header() {
  return (
    <header className="border-b border-border">
      <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="font-bold text-xl">
          Brand
        </Link>

        {/* Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          <Link to="/features" className="text-muted-foreground hover:text-foreground">
            Features
          </Link>
          <Link to="/pricing" className="text-muted-foreground hover:text-foreground">
            Pricing
          </Link>
          <Link to="/docs" className="text-muted-foreground hover:text-foreground">
            Docs
          </Link>
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button variant="ghost">Sign In</Button>
          <Button>Get Started</Button>
        </div>
      </div>
    </header>
  );
}`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Footer Component Example</h2>

          <CodePreview
            title="Simple Footer"
            code={`function Footer() {
  return (
    <footer className="border-t border-border py-12 bg-muted/30">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <h4 className="font-semibold mb-4">Product</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/features">Features</Link></li>
              <li><Link to="/pricing">Pricing</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Company</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/about">About</Link></li>
              <li><Link to="/contact">Contact</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-8 pt-8 border-t border-border text-sm text-muted-foreground">
          © 2025 Company. All rights reserved.
        </div>
      </div>
    </footer>
  );
}`}
            language="tsx"
          />
        </section>

        <Alert>
          <Info className="w-4 h-4" />
          <AlertDescription>
            PageLayout automatically sets <code>min-h-screen</code> and uses flexbox to push the footer to the bottom even with short content.
          </AlertDescription>
        </Alert>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Real-World Example</h2>

          <CodePreview
            title="Complete Landing Page"
            code={`import { PageLayout } from '@voilajsx/uikit/page-layout';
import { Button } from '@voilajsx/uikit/button';
import { Card, CardContent } from '@voilajsx/uikit/card';

function LandingPage() {
  return (
    <PageLayout header={<Header />} footer={<Footer />} stickyHeader>
      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">
            Build Faster with UIKit
          </h1>
          <p className="text-xl text-muted-foreground mb-8">
            Production-ready components for React applications
          </p>
          <div className="flex justify-center gap-4">
            <Button size="lg">Get Started</Button>
            <Button size="lg" variant="outline">Learn More</Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">Features</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2">37+ Components</h3>
                <p className="text-muted-foreground">
                  Everything you need to build modern UIs
                </p>
              </CardContent>
            </Card>
            {/* More cards... */}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}`}
            language="tsx"
          />
        </section>
      </div>
    </TutorialLayout>
  );
};

export default PageLayoutTutorial;
